import Vue from 'vue'
import VueResource from 'vue-resource'
import store from 'store'
import ApiServerMap from './api.js'
import apiSignature from './apiSignature'
import { ERROR_CODE, LOCATION_CODE } from 'const/common'
import nProgress from 'nprogress'
import getDeviceId from 'utils/getDeviceId'
import getEnv from 'utils/getEnv'

const deviceId = getDeviceId()
// http utils
Vue.use(VueResource)
const isProd = getEnv() === 'production'

/**
 * URL 格式替换 ~<API TYPE>/<API PATH>
 * @example ~oauth/auth/refresh => https://oauth.tigerbrokers.com/auth/refresh
 */
const urlReplacement = (request, next) => {
  let serverHost = null

  // Request url that start from ~ will be parsed and replaced with the specify server host
  const matchReg = /^~([^/]+)\//i
  const mat = request.url.match(matchReg) || []
  const apiHosts = ApiServerMap[mat[1]]

  if (apiHosts) {
    // Chose host by env
    serverHost = isProd ? apiHosts.production[0] : apiHosts.test

    request.hostType = mat[1]
    request.url = request.url.replace(matchReg, serverHost + '/')
  }
  next()
}

// 全局添加一些全局的必要信息
const appendEssentialParams = (request, next) => {
  request.params = {
    deviceId,
    appVer: process.env.__version__,
    platform: 'desktop-web',
    vendor: 'web',
    region: LOCATION_CODE.US,
    location: store.getters.location,
    lang: store.getters.languageServerNeed,
    pwd_api_ver: 'v2',
    ...request.params
  }
  next()
}

// 同步更新服务器时间用以本地时间校准
const updateServerTime = (request, next) => {
  next(res => {
    if (res.data) {
      const serverTime = res.data.serverTime || res.data.server_time
      if (serverTime) store.dispatch('updateTime', serverTime)
    }
  })
}

// TradeV2接口添加自定义头信息
const appendTradeV2Header = (request, next) => {
  // 订单和持仓相关接口访问v2接口，可获取窝轮牛熊证数据
  const tradeV2 = [
    '~trade/trade/preview_order',
    '~trade/trade/open_order',
    '~trade/portfolio/pnl',
    '~trade/trade/fill_order',
    '~trade/trade/get_order',
    '~trade/trade/inactive_order',
    '~trade/portfolio/position',
    '~trade/trade/position_order',
    '~trade/portfolio/stat_brief',
    '~trade/portfolio/stat/',
    '~trade/portfolio/risk',
    '~trade/trade/fill_order_month'
  ]
  if (tradeV2.indexOf(request.url) > -1) {
    request.headers.set('X-API-Version', 'v2')
  }
  next()
}

// 检查 Auth 服务是否在维护中，需要显示维护界面
const checkAuthServerMaintenance = (request, next) => {
  if (Vue.http.paused) return
  if (/^~oauth/.test(request.url)) {
    return next(({ status, data }) => {
      if (status === 200) {
        const result = data.data || {}
        if (result.error_code === ERROR_CODE.IN_MAINTENANCE) {
          const msg = result.extra.message || {
            title: '',
            content: result.error_description
          }
          Vue.$showAuthInMaintenance(msg)
          nProgress.done()
          Vue.http.paused = true
        }
      }
    })
  }
}

// 检查 HTTP 的身份校验信息
const checkRequestAccessToken = (request, next) => {
  if (Vue.http.paused) return
  let tryTimes = 0
  const MAX_TRY = 2
  // Trade接口或Auth接口出现 401 时需要重试
  if (/^~(tesseract|oauth|trade|omnibusTrade)/.test(request.url)) {
    return next(({ status, data }) => {
      const result = data.data || {}
      if (
        status === 401 ||
        ERROR_CODE.TOKEN_INVALID.includes(result.error_code)
      ) {
        // eslint-disable-next-line no-console
        console.log(
          'identity> access token is invalid, try next %s time',
          tryTimes
        )

        if (tryTimes <= 0) {
          Vue.http.paused = true
          store.dispatch('logout')
          tryTimes = MAX_TRY
        } else {
          tryTimes--
          // 重新发起请求
          return Vue.http(request)
        }
      }
    })
  }
}

// 添加身份验证信息
const appendAuthHeader = request => {
  if (store.getters.token) {
    request.headers.set('Authorization', `Bearer ${store.getters.token}`)
  }
}

// 新增、修改、取消订单需要传token。匹配ib和omnibus不同接口
const appendTradeHeader = request => {
  const ibRule = /^~trade\/trade\/(modify|place|cancel|preview|check)_order$/
  const bsRule = /^~(omnibus|virtual)Trade\/orders(\/(check|[0-9]+\/(cancel|modify)))?$/
  if (ibRule.test(request.url) || bsRule.test(request.url)) {
    request.headers.set('TradeToken', store.getters.tradeToken)
  }
}

// 模拟账号请求需要添加 __v_account__ 参数
const appendVirtualTradeParams = request => {
  if (/^~virtualTrade\//g.test(request.url)) {
    request.params = {
      ...request.params,
      __v_account__: store.getters.curAccountId
    }
  }
  if (/^~(trade|omnibusTrade)\//g.test(request.url)) {
    // 查订单详情不需要传 account_id，需要查询其他子账户订单详情。
    if (/orders\/\d+$/g.test(request.url)) return
    request.params = {
      ...request.params,
      account_id: store.getters.curAccountId
    }
  }
}

// 错误处理
const errorHandler = (request, next) => {
  const url = request.url

  return next(res => {
    if (res.status === 0) {
      if (/^~(trade|omnibusTrade|virtualTrade)/.test(url)) {
        logRequest('Request failed (status 0)', request)
      }
      return {
        status: 0,
        aborted: true,
        url: request.url,
        message: 'Request canceled'
      }
    }
    if (isRequestFailed(url, res)) {
      const message = res.data?.msg || res.data?.data?.error_message || ''
      logRequest(`Request failed (${res.status}) ${message}`, request)
    }
    return res
  })
}

// 请求出错时记录日志，方便排查问题
const logRequest = (message, request) => {
  Vue.log(new Error(`${message}，${request.url}`), evt => {
    evt.addMetadata('request', {
      url: request.url,
      method: request.method,
      body: extractRequestBodyLogInfo(request.body),
      headers: {
        'x-ca-nonce': request.headers.get('x-ca-nonce'),
        'x-ca-key': request.headers.get('x-ca-key'),
        'x-ca-timestamp': request.headers.get('x-ca-timestamp')
      }
    })
  })
}

// 判断请求是否失败，针对不同接口做不同的判断
const isRequestFailed = (url, res) => {
  if (res.status >= 400) return true
  if (res.status === 200) {
    if (/^~oauth/.test(url)) return !res.data.is_succ
    if (/^~trade/.test(url)) return res.data.ret > 0
    if (/^~omnibusTrade/.test(url)) return res.data.status !== 'ok'
    if (/^~stock/.test(url)) return res.data.ret > 0
  }
  return false
}

// 请求数据脱敏，避免敏感信息泄露到日志中
const extractRequestBodyLogInfo = body => {
  body = body || {}
  if (body instanceof FormData) {
    const entries = body.entries()
    body = {}
    for (const [key, value] of entries) {
      body[key] = value
    }
  } else if (typeof body === 'string') {
    try {
      body = JSON.parse(body)
    } catch (e) {
      body = {}
    }
  }
  delete body.trade_password
  delete body.login_password
  return body
}

const interceptors = [
  updateServerTime,
  appendEssentialParams,
  urlReplacement,
  checkAuthServerMaintenance,
  checkRequestAccessToken,
  appendTradeV2Header,
  appendAuthHeader,
  appendTradeHeader,
  appendVirtualTradeParams,
  errorHandler
]
Vue.http.interceptors.unshift(apiSignature)
Vue.http.interceptors.push(...interceptors)

export default Vue.http
